/**
 * Billing.tsx — Billing & Subscription management page.
 * Route: /billing (protected, customer role)
 *
 * Sections:
 *  1. Trial banner (when trialing with < 7 days)
 *  2. Current plan card with status, actions, next billing
 *  3. Plan comparison (switch plan)
 *  4. Invoice history table
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReceiptIcon from '@mui/icons-material/Receipt';
import StarIcon from '@mui/icons-material/Star';
import TimerIcon from '@mui/icons-material/Timer';
import UpgradeIcon from '@mui/icons-material/Upgrade';

import axios from 'axios';
import API_BASE from '../config/api';
import { useAuth } from '../context/AuthContext';

// ── Types ─────────────────────────────────────────────────────────────────────

interface BillingStatus {
  plan:                  string;
  status:                string;   // trialing | active | past_due | canceled | suspended | none
  trial_end:             string | null;
  next_billing_date:     string | null;
  amount_cents:          number;
  cancel_at_period_end:  boolean;
  days_until_trial_end:  number | null;
}

interface Invoice {
  id:               string;
  stripe_invoice_id: string;
  amount_cents:     number;
  currency:         string;
  status:           string;
  invoice_url:      string | null;
  invoice_pdf:      string | null;
  period_start:     string | null;
  period_end:       string | null;
  paid_at:          string | null;
  created_at:       string;
}

interface Plan {
  display_name:   string;
  price_monthly:  number;
  max_employees:  number;
  scan_frequency: string;
  features:       string[];
  is_popular?:    boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const PLAN_ORDER = ['starter', 'growth', 'professional'];

const FEATURE_LABELS: Record<string, string> = {
  daily_scan:          'Daily security scan',
  weekly_report:       'Weekly AI report',
  basic_score:         'Basic risk score',
  twice_daily_scan:    'Twice-daily security scan',
  daily_report:        'Daily AI report',
  dynamic_score:       'Dynamic risk score',
  autofix_simple:      'Auto-fix simple issues',
  every_6_hours_scan:  'Scanning every 6 hours',
  instant_alerts:      'Instant alerts',
  prediction_7day:     '7-day threat prediction',
  managed_fixes:       'Managed fixes included',
  soc2_reports:        'Full SOC2 reports',
};

function statusColor(s: string): 'success' | 'warning' | 'error' | 'default' | 'primary' {
  if (s === 'active')    return 'success';
  if (s === 'trialing')  return 'primary';
  if (s === 'past_due')  return 'error';
  if (s === 'canceled')  return 'default';
  return 'warning';
}

function statusLabel(s: string): string {
  const m: Record<string, string> = {
    trialing:  'Trial',
    active:    'Active',
    past_due:  'Past Due',
    canceled:  'Canceled',
    suspended: 'Suspended',
    none:      'No subscription',
  };
  return m[s] ?? s;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function fmtAmount(cents: number, currency = 'usd'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

// ── Sub-components ────────────────────────────────────────────────────────────

function InvoiceStatusChip({ status }: { status: string }) {
  const color = status === 'paid' ? 'success' : status === 'open' ? 'warning' : 'error';
  return (
    <Chip
      label={status.charAt(0).toUpperCase() + status.slice(1)}
      size="small"
      color={color as any}
      variant="outlined"
    />
  );
}

function PlanCard({
  planId,
  plan,
  isCurrent,
  isSwitching,
  onSwitch,
}: {
  planId: string;
  plan: Plan;
  isCurrent: boolean;
  isSwitching: boolean;
  onSwitch: (id: string) => void;
}) {
  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 3,
        border: isCurrent ? '2px solid' : '1px solid',
        borderColor: isCurrent ? 'primary.main' : 'rgba(255,255,255,0.08)',
        bgcolor: isCurrent ? 'rgba(0,180,216,0.05)' : 'background.paper',
        position: 'relative',
      }}
    >
      {planId === 'growth' && !isCurrent && (
        <Box sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)' }}>
          <Chip
            icon={<StarIcon sx={{ fontSize: 13 }} />}
            label="Most Popular"
            size="small"
            color="primary"
            sx={{ fontWeight: 700, fontSize: 11 }}
          />
        </Box>
      )}
      {isCurrent && (
        <Box sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)' }}>
          <Chip label="Current Plan" size="small" color="primary" sx={{ fontWeight: 700, fontSize: 11 }} />
        </Box>
      )}
      <CardContent sx={{ p: 3, pt: (planId === 'growth' || isCurrent) ? 4 : 3 }}>
        <Typography variant="h6" fontWeight={700}>{plan.display_name}</Typography>
        <Typography variant="h4" fontWeight={900} color="primary.main" mb={2}>
          ${plan.price_monthly}<Typography component="span" variant="body2" color="text.secondary">/mo</Typography>
        </Typography>
        <Stack spacing={0.75} mb={3}>
          {plan.features.map((f) => (
            <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon sx={{ fontSize: 16, color: 'success.main', flexShrink: 0 }} />
              <Typography variant="body2">{FEATURE_LABELS[f] ?? f}</Typography>
            </Box>
          ))}
        </Stack>
        {!isCurrent && (
          <Button
            fullWidth
            variant="outlined"
            size="small"
            disabled={isSwitching}
            startIcon={<UpgradeIcon />}
            onClick={() => onSwitch(planId)}
            sx={{ fontWeight: 600 }}
          >
            {isSwitching ? <CircularProgress size={16} /> : 'Switch to this plan'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Billing() {
  const { user }      = useAuth();
  const navigate      = useNavigate();
  const [params]      = useSearchParams();

  const [billing, setBilling]       = useState<BillingStatus | null>(null);
  const [invoices, setInvoices]     = useState<Invoice[]>([]);
  const [plans, setPlans]           = useState<Record<string, Plan>>({});
  const [loading, setLoading]       = useState(true);
  const [msg, setMsg]               = useState('');
  const [msgSev, setMsgSev]         = useState<'success' | 'error' | 'info'>('info');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [switching, setSwitching]   = useState('');

  const customerId = user?.customer_id;

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const [bRes, iRes, pRes] = await Promise.all([
        axios.get(`${API_BASE}/billing/${customerId}/status`),
        axios.get(`${API_BASE}/billing/${customerId}/invoices`),
        axios.get(`${API_BASE}/plans`),
      ]);
      setBilling(bRes.data);
      setInvoices(iRes.data);
      // Convert array → Record<plan_key, plan>
      const planMap: Record<string, Plan> = {};
      (pRes.data as any[]).forEach((p: any) => { planMap[p.plan_key] = p; });
      setPlans(planMap);
    } catch {
      setMsg('Failed to load billing information.');
      setMsgSev('error');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  // Handle Stripe redirect + paused redirect
  useEffect(() => {
    if (params.get('success') === '1') {
      setMsg('Payment method added! Your subscription is now active.');
      setMsgSev('success');
      load();
    } else if (params.get('canceled') === '1') {
      setMsg('Checkout canceled — no changes were made.');
      setMsgSev('info');
    }
  }, [params]); // eslint-disable-line

  const isPausedRedirect = params.get('reason') === 'paused';

  async function openCheckout(plan: string) {
    if (!customerId) return;
    try {
      const { data } = await axios.post(`${API_BASE}/billing/${customerId}/checkout`, {
        plan,
        success_url: `${window.location.origin}/billing?success=1`,
        cancel_url:  `${window.location.origin}/billing?canceled=1`,
      });
      window.location.href = data.checkout_url;
    } catch {
      setMsg('Could not open checkout. Please try again.');
      setMsgSev('error');
    }
  }

  async function openPortal() {
    if (!customerId) return;
    try {
      const { data } = await axios.post(`${API_BASE}/billing/${customerId}/portal`, {
        return_url: `${window.location.origin}/billing`,
      });
      window.location.href = data.portal_url;
    } catch {
      setMsg('Could not open billing portal. Please try again.');
      setMsgSev('error');
    }
  }

  async function cancelSubscription() {
    if (!customerId) return;
    setCancelOpen(false);
    try {
      await axios.post(`${API_BASE}/billing/${customerId}/cancel`);
      setMsg('Your subscription will be canceled at the end of the current billing period.');
      setMsgSev('info');
      load();
    } catch {
      setMsg('Could not cancel subscription.');
      setMsgSev('error');
    }
  }

  async function switchPlan(newPlan: string) {
    if (!customerId) return;
    setSwitching(newPlan);
    try {
      await axios.post(`${API_BASE}/billing/${customerId}/upgrade`, { new_plan: newPlan });
      setMsg(`Plan updated to ${newPlan.charAt(0).toUpperCase() + newPlan.slice(1)}!`);
      setMsgSev('success');
      load();
    } catch {
      setMsg('Could not switch plan.');
      setMsgSev('error');
    } finally {
      setSwitching('');
    }
  }

  if (!customerId) {
    return (
      <Box p={3}>
        <Alert severity="info">
          Billing is only available for customer accounts. Engineers and admins do not have a subscription.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box p={4} display="flex" alignItems="center" gap={2}>
        <CircularProgress size={24} />
        <Typography color="text.secondary">Loading billing…</Typography>
      </Box>
    );
  }

  const daysLeft  = billing?.days_until_trial_end ?? null;
  const isTrialing = billing?.status === 'trialing';
  const showUrgentBanner = isTrialing && daysLeft !== null && daysLeft <= 7;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={3} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CreditCardIcon color="primary" /> Billing & Subscription
      </Typography>

      {/* Paused redirect notice */}
      {isPausedRedirect && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Your account is paused. Add a payment method below to reactivate instantly.
          Your data is preserved for 30 days from the date your account was paused.
        </Alert>
      )}

      {/* Flash message */}
      {msg && (
        <Alert severity={msgSev} sx={{ mb: 3 }} onClose={() => setMsg('')}>{msg}</Alert>
      )}

      {/* ── Trial urgent banner ─────────────────────────────────────────── */}
      {showUrgentBanner && (
        <Alert
          icon={<TimerIcon />}
          severity={daysLeft! <= 1 ? 'error' : daysLeft! <= 3 ? 'warning' : 'warning'}
          sx={{ mb: 3, fontWeight: 500 }}
          action={
            <Button color="inherit" size="small" variant="outlined" sx={{ fontWeight: 700 }}
              onClick={() => openCheckout(billing?.plan ?? 'growth')}>
              Add Payment Method →
            </Button>
          }
        >
          {daysLeft === 0
            ? 'Your trial expires today! Add a payment method now to avoid interruption.'
            : daysLeft === 1
            ? 'Your trial expires tomorrow — add a payment method to keep your account active.'
            : `${daysLeft} days left in your free trial — add a payment method to continue after trial ends.`}
        </Alert>
      )}

      {/* ── Section 1: Current Plan ─────────────────────────────────────── */}
      <Paper sx={{ p: 3, borderRadius: 2, mb: 3, border: '1px solid rgba(0,180,216,0.12)' }}>
        <Typography variant="h6" fontWeight={700} mb={2}>Current Plan</Typography>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="h4" fontWeight={800} sx={{ textTransform: 'capitalize' }}>
                {billing?.plan ?? '—'}
              </Typography>
              {billing && (
                <Chip
                  label={statusLabel(billing.status)}
                  color={statusColor(billing.status) as any}
                  size="small"
                  sx={{ fontWeight: 700 }}
                />
              )}
            </Box>

            {isTrialing && daysLeft !== null && (
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary" mb={0.5}>
                  Trial ends {fmtDate(billing?.trial_end ?? null)}
                  {daysLeft > 0 ? ` (${daysLeft} days left)` : ' (today)'}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={Math.max(0, 100 - (daysLeft / 14) * 100)}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: 'rgba(255,255,255,0.08)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: daysLeft <= 3 ? 'error.main' : daysLeft <= 7 ? '#f77f00' : 'primary.main',
                    },
                  }}
                />
              </Box>
            )}

            {!isTrialing && billing?.status === 'active' && (
              <Typography variant="body2" color="text.secondary" mb={1}>
                Next billing: {fmtDate(billing.next_billing_date)} · {fmtAmount(billing.amount_cents)}/month
              </Typography>
            )}

            {billing?.cancel_at_period_end && (
              <Chip
                label="Cancels at period end"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ mb: 1 }}
              />
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Stack spacing={1.5}>
              {(billing?.status === 'trialing' || billing?.status === 'active' || billing?.status === 'past_due') && (
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<CreditCardIcon />}
                  onClick={() => openCheckout(billing?.plan ?? 'growth')}
                  sx={{ fontWeight: 700 }}
                >
                  {isTrialing ? 'Add Payment Method' : 'Update Payment Method'}
                </Button>
              )}

              <Button
                variant="outlined"
                fullWidth
                startIcon={<ReceiptIcon />}
                onClick={openPortal}
                sx={{ fontWeight: 600 }}
              >
                Manage Payment & Invoices
              </Button>

              {billing?.status !== 'canceled' && !billing?.cancel_at_period_end && (
                <Button
                  variant="text"
                  fullWidth
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={() => setCancelOpen(true)}
                  size="small"
                  sx={{ fontWeight: 600 }}
                >
                  Cancel Subscription
                </Button>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* ── Section 2: Plan comparison ──────────────────────────────────── */}
      <Typography variant="h6" fontWeight={700} mb={2}>Available Plans</Typography>
      <Grid container spacing={2} mb={4} alignItems="stretch">
        {PLAN_ORDER.map((planId) => {
          const plan = plans[planId];
          if (!plan) return null;
          return (
            <Grid item xs={12} md={4} key={planId}>
              <PlanCard
                planId={planId}
                plan={plan}
                isCurrent={billing?.plan === planId}
                isSwitching={switching === planId}
                onSwitch={switchPlan}
              />
            </Grid>
          );
        })}
      </Grid>

      <Divider sx={{ mb: 4 }} />

      {/* ── Section 3: Invoice history ──────────────────────────────────── */}
      <Typography variant="h6" fontWeight={700} mb={2}>
        Invoice History
      </Typography>

      {invoices.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <ReceiptIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No invoices yet — they'll appear here once your trial ends.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Period</TableCell>
                <TableCell align="right">Download</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((inv) => (
                <TableRow key={inv.id} hover>
                  <TableCell>
                    <Typography variant="body2">{fmtDate(inv.created_at)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {fmtAmount(inv.amount_cents, inv.currency)}
                    </Typography>
                  </TableCell>
                  <TableCell><InvoiceStatusChip status={inv.status} /></TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {inv.period_start ? `${fmtDate(inv.period_start)} – ${fmtDate(inv.period_end)}` : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      {inv.invoice_url && (
                        <Tooltip title="View invoice">
                          <IconButton size="small" href={inv.invoice_url} target="_blank" rel="noopener noreferrer">
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {inv.invoice_pdf && (
                        <Tooltip title="Download PDF">
                          <IconButton size="small" href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer">
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!inv.invoice_url && !inv.invoice_pdf && (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Cancel confirmation dialog ──────────────────────────────────── */}
      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel subscription?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Your subscription will remain active until the end of your current billing period.
            After that, all scans and monitoring will stop. Your data is preserved for 30 days.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>Keep subscription</Button>
          <Button variant="contained" color="error" onClick={cancelSubscription}>
            Cancel at period end
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
